import type { RouteLocationNormalizedGeneric, RouteLocationNormalizedLoadedGeneric, RouteLocationRaw } from 'vue-router'

import { useHubspotTracking } from './composables/useHubspotTracking.ts'
import { useContentsquareTracking } from './composables/useContentsquareTracking.ts'
import { IndexedDBCache } from './services/cache/IndexedDBCache.ts'

let routerGuardsEnabled = false

const { trackPageView: trackHubspotPageView } = useHubspotTracking()
const { trackPageView: trackContentsquarePageView } = useContentsquareTracking()

export async function routerGuards(route: RouteLocationNormalizedGeneric): Promise<string | Error | RouteLocationRaw | undefined> {
  if (isOneTimeCase() && !route.fullPath.includes('one-time-case')) {
    // One time case users can only access the one time case page.
    return Error('Unauthorized route')
  }

  if (!stores.user.authenticated && !route.meta.guest) {
    // Non authenticated users that go to non-guest routes need to sign up.
    return { path: '/login', query: { next: route.fullPath } }
  } else if (stores.user.authenticated && route.meta.guest && !route.meta.allowSignedIn) {
    // Authenticated users cannot access guest routes.
    return String(route.query?.next ?? '/')
  }

  // When a user opens a link that needs to be in a certain organization,
  // we can handle the switching to that organization using a query parameter.
  if (route.query.organization_id) {
    const organizationId = Number(route.query.organization_id)
    if (!stores.user.user.organizations.some((organization) => organization.id === organizationId)) {
      return '/'
    }

    // Delete the organization_id from the query before switching to prevent an infinite loop.
    // The organization switch will call checkCurrentRoute again and if we don't delete it, it will try to switch again and keep waiting.
    const query = route.query
    delete query.organization_id
    await stores.organization.switch(organizationId)
    await IndexedDBCache.tags('organization').put(stores.user.user.id, stores.organization.id)

    return { path: route.path, query }
  }

  if (route.meta.organizationRequired && !stores.organization.id) {
    return '/inactive-organisation'
  }

  if (route.meta.permission && !can(route.meta.permission)) {
    return Error(`Unauthorized route: missing permission ${route.meta.permission}`)
  }

  const hasRouteService = {
    dataDirectory: stores.organization.has_data_directory,
    passportVerification: stores.organization.has_passport_verification,
    implicitFeedback: stores.organization.has_implicit_feedback,
  }
  if (route.meta.service && !hasRouteService[route.meta.service as keyof typeof hasRouteService]) {
    return Error(`Unauthorized route: missing service ${route.meta.service}`)
  }

  if (route.meta.visible && !route.meta.visible()) {
    return Error(`Unauthorized route: not visible ${route.path}`)
  }

  if (route.meta.module && !$hasModule(route.meta.module)) {
    return Error(`Unauthorized route: missing module ${route.meta.module}`)
  }

  if (stores.user.authenticated && typeof route.query.notification_id === 'string') {
    await api.post('notifications/read', { ids: [route.query.notification_id] })

    return { path: route.path, query: { ...route.query, notification_id: undefined }, hash: route.hash }
  }

  // When a user tries to access an organization that they are not in, without explicitly enabled ghost mode or without consent, switch back to an organization that they are in.
  const organizationId = Number(route.params.organizationId ?? stores.organization.id)
  if (
    organizationId &&
    route.meta.organizationRequired &&
    stores.user.authenticated &&
    !route.path.startsWith('/administration/organizations/') &&
    !stores.user.user.organizations.some((organization) => organization.id === organizationId) &&
    (!(await IndexedDBCache.tags('globals').get('ghostMode')) || !stores.organization.account_access_consent)
  ) {
    IndexedDBCache.tags('organization').flush() // Clear cached organization to prevent repeated redirects.
    window.location.replace('/') // Use full page reload to avoid infinite loop.
  }
}

export async function checkCurrentRoute() {
  if (!routerGuardsEnabled) {
    return
  }

  const result = await routerGuards($router.currentRoute.value)
  if (result instanceof Error) {
    await $router.replace('/')
  } else if (result) {
    try {
      await $router.replace(result)
    } catch {
      await $router.replace('/')
    }
  }
}

export async function onAfterRouteChange(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric) {
  if (from.meta.guest && !to.meta.guest) {
    // When we are logging in or after a password reset, a special transition is used.
    to.meta.transition = 'v-slide-out-right-fade'
  } else {
    to.meta.transition = 'v-fade-fast'
  }

  if (to.meta.title) {
    setDocumentTitle(to.meta.title)
  }

  // Track page views after title is set.
  if (to.fullPath !== from.fullPath) {
    trackHubspotPageView(`/#${to.fullPath}`)
    trackContentsquarePageView()
  }

  if (history.value.at(-2) === to.fullPath) {
    // Handle going back to previous page.
    history.value.splice(history.value.length - 1, 1)
  } else if (to.fullPath !== from.fullPath) {
    history.value.push(to.fullPath)
  }

  userpilotClient.reload()

  if (to.hash) {
    await delay(100)
    const el = document.getElementById(to.hash.slice(1))
    el?.scrollIntoView({ behavior: 'smooth' })
  } else {
    if (from.path.startsWith('/onboarding-forms/') && to.path.startsWith('/onboarding-forms/')) {
      return
    }
    document.querySelector('.main-container')?.scrollTo({ top: 0 })
  }
}

import { userpilotClient } from './services/UserpilotClient.ts'

export const history = ref<string[]>([])
export function createRouterGuards() {
  routerGuardsEnabled = true
  history.value = [$router.currentRoute.value.fullPath]
  $router.beforeEach(async (to) => await routerGuards(to))
  $router.afterEach((to, from) => onAfterRouteChange(to, from))
}

/**
 * Go back to the previous page in our own history stack.
 */
export async function back() {
  const previous = history.value.at(-2)
  if (previous) {
    history.value.splice(history.value.length - 2, 2)
    await $router.replace(previous)
  } else {
    $router.back()
  }
}

export async function replace(route: RouteLocationRaw) {
  if (history.value.length > 0) {
    const fullPath = $router.resolve(route).fullPath
    if (history.value.at(-2) !== fullPath) {
      history.value.splice(history.value.length - 1, 1)
    }
  }
  await $router.replace(route)
}

/**
 * Go back in the history, or replace the current route with the fallback route.
 * If the current route is a detail route, the route will be replaced with the list route.
 * @param fallback Fallback route to use if there is no history or the current route is not a detail route.
 */
export async function backWithFallback(fallback?: string) {
  if (history.value.length > 1) {
    await back()
  } else if (fallback) {
    await $router.replace(fallback)
  } else if ($route.value.name?.toString().endsWith('.id')) {
    await $router.replace({ name: $route.value.name.toString().slice(0, -3) })
  } else {
    await $router.replace(app.config.globalProperties.$inAdministration ? '/administration/home' : '/')
  }
}
